import { formatUnit } from '../../utils/units';
import { ACTIVE_QUANTITY_UNITS } from '../../utils/commercialQuantity';

// Single home for supplier-order line-item units, shared by the create wizard
// and the detail/list pages so option lists and display labels never drift.
export const SUPPLIER_ORDER_UNITS = ACTIVE_QUANTITY_UNITS;

export type SupplierOrderUnit = typeof SUPPLIER_ORDER_UNITS[number];

const getBaseLanguage = (language?: string): string => (language || '').split('-')[0].toLowerCase();

export const formatSupplierOrderQuantityValue = (
  quantity: number | string | null | undefined,
  language?: string,
): string => {
  if (quantity === null || quantity === undefined || quantity === '') return '';

  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity)) return String(quantity ?? '');

  return new Intl.NumberFormat(getBaseLanguage(language) === 'pl' ? 'pl-PL' : 'en-US', {
    maximumFractionDigits: 3,
  }).format(numericQuantity);
};

// Compatibility wrapper for existing supplier-order quantity callers. Labels
// still come from the canonical frontend unit formatter in utils/units.ts.
export const formatSupplierOrderUnit = (
  unit: string,
  _t: (key: string) => string,
  language?: string
): string => formatUnit(unit, language);

// A supplier-order line stores quantity in its own unit. Unlike WZ, it is not
// inherently a piece count: `2 m3` means two cubic metres, not two pieces with
// a presentation conversion.
export interface SupplierOrderQuantityInput {
  unit?: string | null;
  quantity?: number | string | null;
  width?: number | string | null;
  length?: number | string | null;
  thickness?: number | string | null;
}

// Full quantity label for list previews, selectors, and wizard summaries.
export const formatSupplierOrderQuantity = (
  item: SupplierOrderQuantityInput,
  t: (key: string) => string,
  language?: string,
): string => {
  const unit = item.unit || 'pcs';
  const quantity = formatSupplierOrderQuantityValue(item.quantity, language);
  return `${quantity} ${formatSupplierOrderUnit(unit, t, language)}`.trim();
};
